import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { UserService } from '../../../api/services/user.service';
import {
  adminUpdateUser,
  adminUpdateUserFailed,
  adminUpdateUserSuccess,
  createUser,
  createUserFailed,
  createUserSuccess,
  loadUsers,
  readAllFailed,
  readAllSuccess,
  updateUser,
  UserActionTypes,
} from '../actions/user.actions';
import { userProfile } from '../../../authentication/state/actions/authentication.actions';
import { UserQueryDto } from '../../../api/models/user-query-dto';

@Injectable()
export class UserEffects {
  private readonly actions$ = inject(Actions);
  private readonly userService = inject(UserService);
  private readonly store = inject(Store);
  private readonly messageService = inject(MessageService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUsers),
      switchMap(() =>
        this.userService.listUsers({ body: { unpaged: true } }).pipe(
          // NOSONAR
          map((users) => readAllSuccess({ users: users.content ?? [] })),
          catchError((error) => of(readAllFailed({ error })))
        )
      )
    )
  );

  loadUsersFailedToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(readAllFailed),
        tap(() =>
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to read Users.',
          })
        )
      ),
    { dispatch: false }
  );

  createUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createUser),
      exhaustMap(({ newUser }) =>
        this.userService.createUser({ body: newUser }).pipe(
          map((created) => createUserSuccess({ newUser: created })),
          catchError((error) => {
            const body = error?.error;
            const message =
              (Array.isArray(body?.errors) ? body.errors[0] : null) ||
              body?.message ||
              error?.message ||
              'Failed to create user.';
            return of(createUserFailed({ error: message }));
          })
        )
      )
    )
  );

  createUserSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createUserSuccess),
        tap(() =>
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Successfully created new user.',
          })
        )
      ),
    { dispatch: false }
  );

  createUserFailedToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createUserFailed),
        tap((action) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: (action.error as string) ?? 'Failed to create user.',
          });
        })
      ),
    { dispatch: false }
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUser),
      exhaustMap(({ newUser }) =>
        this.userService.updateOwnUser({ body: newUser }).pipe(
          map((updatedUser) => {
            this.store.dispatch(
              userProfile({
                userProfile: {
                  firstName: newUser.firstName,
                  lastName: newUser.lastName,
                  userInvitation: { status: 'accepted' },
                  lastModifiedByDate: updatedUser.lastModifiedByDate,
                } as UserQueryDto,
              })
            );
            return { type: UserActionTypes.UPDATE_USER_SUCCESS };
          }),
          catchError(() => of({ type: UserActionTypes.UPDATE_USER_FAILED }))
        )
      )
    )
  );

  adminUpdateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminUpdateUser),
      exhaustMap(({ userId, update }) =>
        this.userService.adminUpdateUser({ userId, body: update }).pipe(
          map(() => adminUpdateUserSuccess()),
          catchError((error) => {
            const body = error?.error;
            const message =
              (Array.isArray(body?.errors) ? body.errors[0] : null) ||
              body?.message ||
              error?.message ||
              'Failed to update user.';
            return of(adminUpdateUserFailed({ error: message }));
          })
        )
      )
    )
  );

  adminUpdateUserSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(adminUpdateUserSuccess),
        tap(() =>
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'User updated successfully.',
          })
        )
      ),
    { dispatch: false }
  );

  adminUpdateUserFailedToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(adminUpdateUserFailed),
        tap((action) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: (action.error as string) ?? 'Failed to update user.',
          });
        })
      ),
    { dispatch: false }
  );
}
